import type { SxProps, Theme } from '@mui/material';

/**
 * SEE-226 shared passkey-management styling (Figma 2840:49940).
 *
 * The web tab and the mobile page render different layouts — a four-column table
 * vs stacked cards — but must read as the same feature, so every colour, type
 * ramp and rule lives here rather than being restated per surface.
 */

/** Body copy: Figma body/medium (14px / 20px / 0.25px, neutral-variant25). */
export const CELL_TEXT = {
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.25px',
  color: 'neutralVariant25.main',
} as const;

/** Secondary/meta copy — the same ramp one step down. */
export const META_TEXT = {
  ...CELL_TEXT,
  fontSize: '12px',
  color: 'text.secondary',
} as const;

/** Table header: Figma label/medium (12px / 16px / 0.5px) on the primary navy. */
export const HEADER_TEXT = {
  fontSize: '12px',
  fontWeight: 510,
  lineHeight: '16px',
  letterSpacing: '0.5px',
  color: 'common.white',
} as const;

/**
 * Every rule — horizontal and vertical, web and mobile — is a 1px inset shadow
 * rather than a border.
 *
 * Borders meant the two directions were separate declarations on separate
 * elements, and because the flexible columns land on fractional pixels while row
 * edges land on whole ones, they rasterised at visibly different weights. One
 * mechanism, one colour, one width leaves nothing to diverge.
 */
/** Figma neutral90. Used directly when the surrounding theme lacks the custom
 * palette key, so a rule never takes the whole surface down with it. */
const RULE_COLOR_FALLBACK = '#E4E2E5';

const ruleColor = (theme: Theme) => theme.palette.neutral90?.main ?? RULE_COLOR_FALLBACK;

export const rowRule = (theme: Theme) => `inset 0 1px 0 ${ruleColor(theme)}`;
export const columnRule = (theme: Theme) => `inset 1px 0 0 ${ruleColor(theme)}`;

/** Rounded, hairline-bordered container shared by the table and the mobile list. */
export const CONTAINER_SX: SxProps<Theme> = {
  border: '1px solid',
  borderColor: 'neutral90.main',
  borderRadius: 2,
  overflow: 'hidden',
  bgcolor: 'common.white',
};

/**
 * "Add Passkey" — Figma I4003:71587;63:490 geometry (8px radius, 8px gap, label
 * 14px/510/20px/0.1px, same navy as the table header), sized down from the
 * design's 48px on request. Without this the theme's sizeMedium renders a 46px
 * pill with 24px padding.
 */
export const ADD_BUTTON_SX: SxProps<Theme> = {
  height: 40,
  px: 1.75,
  borderRadius: 2,
  gap: 1,
  fontSize: '14px',
  fontWeight: 510,
  lineHeight: '20px',
  letterSpacing: '0.1px',
  whiteSpace: 'nowrap',
  bgcolor: 'primary.dark',
  '&:hover': { bgcolor: 'primary.darker' },
  // Zero MUI's own startIcon margins so `gap` alone sets the spacing and the
  // side padding stays exact.
  '& .MuiButton-startIcon': { margin: 0 },
  '& .MuiButton-startIcon > svg': { fontSize: 18 },
};

/**
 * Full-height, full-width delete affordance: the entire cell / card edge is the
 * hit area and the hover target, so there is no small icon to aim at.
 */
export const DELETE_BUTTON_SX: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  borderRadius: 0,
};
